if (!customElements.get('rs7gt-launch-banner')) {
  customElements.define('rs7gt-launch-banner', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.launchRoot = this.closest('rs7gt-launch');
      this.media = this.querySelector('[data-banner-media]');
      this.content = this.querySelector('[data-banner-content]');
      this.animations = [];
      this.motionEnabled = null;
      const options = { signal: this.controller.signal };
      this.motionQuery.addEventListener('change', () => this.updateMotion(), options);
      this.launchRoot?.addEventListener('rs7gt:motionchange', () => {
        cancelAnimationFrame(this.motionFrame);
        // Design's pin is rebuilt after this event; measure the settled layout.
        this.motionFrame = requestAnimationFrame(() => {
          this.motionFrame = 0;
          if (this.isConnected) this.updateMotion();
        });
      }, options);
      this.launchRoot?.addEventListener('rs7gt:pagecoveragechange', () => this.requestTextReveal(), options);
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.refresh(), 160);
      }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      window.addEventListener('load', () => this.refresh(), options);
      this.querySelectorAll('img').forEach(image => {
        if (!image.complete) image.addEventListener('load', () => this.refresh(), options);
      });
      document.fonts?.ready.then(() => {
        if (!options.signal.aborted) this.refresh();
      });
      this.updateMotion();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.resizeTimer);
      cancelAnimationFrame(this.motionFrame);
      cancelAnimationFrame(this.textFrame);
      this.motionFrame = this.textFrame = 0;
      this.destroyMotion();
      this.motionEnabled = null;
    }

    updateMotion() {
      const editor = this.dataset.designMode === 'true' || this.launchRoot?.dataset.designMode === 'true';
      const enabled = !!(!this.motionQuery.matches && !editor && window.gsap && window.ScrollTrigger);
      if (enabled === this.motionEnabled) {
        this.refresh();
        return;
      }
      this.destroyMotion();
      this.motionEnabled = enabled;
      if (!enabled) return;
      try {
        window.gsap.registerPlugin(window.ScrollTrigger);
        this.createMotion();
        this.refresh();
      } catch (_) {
        this.destroyMotion();
        this.motionEnabled = false;
      }
    }

    createMotion() {
      const gsap = window.gsap;
      if (this.media && !this.hasAttribute('data-banner-full-image')) {
        this.animations.push(gsap.fromTo(this.media,
          { scale: 1.08, yPercent: -3 },
          { scale: 1, yPercent: 3, ease: 'none', scrollTrigger: {
            trigger: this,
            start: () => this.flowTop(this) - window.innerHeight,
            end: () => this.flowTop(this) + this.getBoundingClientRect().height,
            scrub: 0.6, invalidateOnRefresh: true
          } }));
      }
      if (!this.content || this.hasAttribute('data-banner-revealed')) return;
      gsap.set(this.content, { autoAlpha: 0, y: 20 });
      const entry = gsap.timeline({ scrollTrigger: {
        trigger: this.content,
        start: () => this.flowTop(this.content) - window.innerHeight * 0.9,
        end: () => this.flowTop(this.content) + this.content.getBoundingClientRect().height,
        toggleActions: 'play none none none'
      } });
      this.animations.push(entry);
      entry.set(this.content, { visibility: 'visible' }, 0)
        .fromTo(this.content, { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 0)
        .call(() => {
          this.setAttribute('data-banner-revealed', '');
          this.requestTextReveal();
        }, null, 0);
    }

    flowTop(element) {
      return this.launchRoot?.flowTop?.(element) ?? element.getBoundingClientRect().top + window.scrollY;
    }

    requestTextReveal() {
      if (!this.motionEnabled || !this.hasAttribute('data-banner-revealed')) return;
      cancelAnimationFrame(this.textFrame);
      // The launch controller creates its shared clock instance after dispatching
      // motionchange. Wait one frame so newly inserted text joins that instance.
      this.textFrame = requestAnimationFrame(() => {
        this.textFrame = 0;
        if (!this.isConnected || !this.motionEnabled || document.hidden
          || (this.launchRoot?.sceneVisibility?.(this) ?? 1) <= 0) return;
        this.querySelectorAll('[data-clock-reveal][data-clock-manual]').forEach(element => {
          const bounds = element.getBoundingClientRect();
          if (bounds.bottom <= 0 || bounds.top >= window.innerHeight
            || bounds.right <= 0 || bounds.left >= window.innerWidth
            || getComputedStyle(element).visibility === 'hidden') return;
          this.launchRoot?.clockReveal?.reveal(element);
        });
      });
    }

    refresh() {
      if (!this.isConnected || !this.motionEnabled) return;
      this.animations.forEach(animation => animation.scrollTrigger?.refresh());
      this.requestTextReveal();
    }

    destroyMotion() {
      cancelAnimationFrame(this.textFrame);
      this.textFrame = 0;
      (this.animations || []).forEach(animation => {
        animation.scrollTrigger?.kill(true);
        animation.kill();
      });
      this.animations = [];
      const targets = [this.media, this.content].filter(Boolean);
      if (targets.length && window.gsap) window.gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
      else targets.forEach(target => {
        ['transform', 'opacity', 'visibility'].forEach(property => target.style.removeProperty(property));
      });
    }
  });
}
